import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Button from '../../components/common/Button';
import FormField from '../shared/FormField';
import {
  courtCasesApi,
  clientsApi,
  departmentsApi,
  caseTypesApi,
  courtTypesApi,
  courtsApi,
  caseStatusesApi,
  judgesApi,
  documentsApi,
  extractList,
} from '../../api/facades';
import * as Fi from 'react-icons/fi';

type IconProps = React.SVGAttributes<SVGElement>;
const FiUpload = Fi.FiUpload as React.FC<IconProps>;

const inputClass =
  'w-full rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm text-slate-700 dark:text-slate-200 focus:border-primary-300 focus:outline-none';

const CourtCaseCreatePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [advo_client_id, setAdvoClientId] = useState<number | ''>('');
  const [department_id, setDepartmentId] = useState<number | ''>('');
  const [client_position, setClientPosition] = useState<'Respondent' | 'Petitioner'>('Petitioner');
  const [party_name, setPartyName] = useState('');
  const [party_lawyer, setPartyLawyer] = useState('');
  const [case_number, setCaseNumber] = useState('');
  const [case_types, setCaseTypes] = useState<number | ''>('');
  const [case_sub_type, setCaseSubType] = useState<number | ''>('');
  const [case_status, setCaseStatus] = useState<number | ''>('');
  const [priority, setPriority] = useState<'High' | 'Medium' | 'Low'>('Medium');
  const [court_type, setCourtType] = useState<number | ''>('');
  const [court, setCourt] = useState<number | ''>('');
  const [judge_type, setJudgeType] = useState<number | ''>('');
  const [remark, setRemark] = useState('');
  const [description, setDescription] = useState('');
  const [date_of_service, setDateOfService] = useState('');
  const [documents, setDocuments] = useState<File[]>([]);

  useEffect(() => {
    const state = (location.state ?? {}) as any;
    if (state?.date && typeof state.date === 'string') setDateOfService(state.date);
  }, []);

  const { data: clientsRes } = useQuery({ queryKey: ['clients'], queryFn: () => clientsApi.list({}) });
  const clients = extractList(clientsRes);

  const { data: deptsRes } = useQuery({ queryKey: ['departments'], queryFn: () => departmentsApi.list() });
  const departments = extractList(deptsRes);

  const { data: caseTypesRes } = useQuery({ queryKey: ['case-types'], queryFn: () => caseTypesApi.list({}) });
  const caseTypesList = extractList(caseTypesRes);

  const { data: caseStatusesRes } = useQuery({ queryKey: ['case-statuses'], queryFn: () => caseStatusesApi.list() });
  const caseStatusesList = extractList(caseStatusesRes);

  const { data: courtTypesRes } = useQuery({ queryKey: ['court-types'], queryFn: () => courtTypesApi.list({}) });
  const courtTypesList = extractList(courtTypesRes);

  const { data: courtsRes } = useQuery({
    queryKey: ['courts', court_type],
    queryFn: () => courtsApi.list({ courtTypeId: typeof court_type === 'number' ? court_type : undefined }),
  });
  const courtsList = extractList(courtsRes);

  const { data: judgesRes } = useQuery({ queryKey: ['judges'], queryFn: () => judgesApi.list({}) });
  const judgesList = extractList(judgesRes);

  useEffect(() => {
    if (caseStatusesList.length > 0 && case_status === '') {
      setCaseStatus(caseStatusesList[0].id);
    }
  }, [caseStatusesList, case_status]);

  useEffect(() => {
    if (judgesList.length > 0 && judge_type === '') {
      setJudgeType(judgesList[0].id);
    }
  }, [judgesList, judge_type]);

  const create = useMutation({
    mutationFn: () =>
      courtCasesApi.create({
        advo_client_id: advo_client_id as number,
        department_id: department_id === '' ? null : department_id,
        client_position,
        party_name,
        party_lawyer,
        case_number: case_number || null,
        case_types: case_types as number,
        case_sub_type: case_sub_type === '' ? null : case_sub_type,
        case_status: case_status as number,
        priority,
        court_type: court_type as number,
        court: court as number,
        judge_type: judge_type as number,
        remark: remark || null,
        description: description || null,
        date_of_service: date_of_service || null,
      }),
    onSuccess: async (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['court-cases'] });
      const id = data?.data?.id ?? (data?.data && (data.data as any).id);
      if (id && documents.length > 0) {
        try {
          await Promise.all(documents.map((file) => documentsApi.upload(id, file)));
          toast.success('Court case and documents created.');
        } catch (e: any) {
          toast.warning('Case created; some document uploads failed.');
        }
        navigate(`/dashboard/court-cases/${id}`);
      } else {
        if (id) navigate(`/dashboard/court-cases/${id}`);
        else navigate('/dashboard/court-cases');
        toast.success('Court case created.');
      }
    },
    onError: (e: any) => toast.error(e?.message || 'Create failed.'),
  });

  const canSubmit =
    typeof advo_client_id === 'number' &&
    party_name.trim() &&
    party_lawyer.trim() &&
    typeof case_types === 'number' &&
    typeof case_status === 'number' &&
    typeof court_type === 'number' &&
    typeof court === 'number' &&
    typeof judge_type === 'number';

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    create.mutate();
  };

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <h1 className="text-xl font-bold text-slate-700 dark:text-slate-200 mb-4">New Court Case</h1>
      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField label="Client" hint="Required">
          <select className={inputClass} value={advo_client_id === '' ? '' : advo_client_id} onChange={(e) => setAdvoClientId(e.target.value === '' ? '' : Number(e.target.value))} required>
            <option value="">Select client</option>
            {clients.map((c: any) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Department">
          <select className={inputClass} value={department_id === '' ? '' : department_id} onChange={(e) => setDepartmentId(e.target.value === '' ? '' : Number(e.target.value))}>
            <option value="">— None —</option>
            {departments.map((d: any) => (
              <option key={d.id} value={d.id}>{d.department_name}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Client position">
          <select className={inputClass} value={client_position} onChange={(e) => setClientPosition(e.target.value as 'Respondent' | 'Petitioner')}>
            <option value="Petitioner">Petitioner</option>
            <option value="Respondent">Respondent</option>
          </select>
        </FormField>
        <FormField label="Party name">
          <input className={inputClass} value={party_name} onChange={(e) => setPartyName(e.target.value)} required />
        </FormField>
        <FormField label="Party lawyer">
          <input className={inputClass} value={party_lawyer} onChange={(e) => setPartyLawyer(e.target.value)} required />
        </FormField>
        <FormField label="Case number">
          <input className={inputClass} value={case_number} onChange={(e) => setCaseNumber(e.target.value)} />
        </FormField>
        <FormField label="Case type" hint="Required">
          <select className={inputClass} value={case_types === '' ? '' : case_types} onChange={(e) => setCaseTypes(e.target.value === '' ? '' : Number(e.target.value))} required>
            <option value="">Select</option>
            {caseTypesList.map((t: any) => (
              <option key={t.id} value={t.id}>{t.case_type_name}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Case sub-type">
          <select className={inputClass} value={case_sub_type === '' ? '' : case_sub_type} onChange={(e) => setCaseSubType(e.target.value === '' ? '' : Number(e.target.value))}>
            <option value="">— None —</option>
            {caseTypesList.map((t: any) => (
              <option key={t.id} value={t.id}>{t.case_type_name}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Case status" hint="Required">
          <select className={inputClass} value={case_status === '' ? '' : case_status} onChange={(e) => setCaseStatus(e.target.value === '' ? '' : Number(e.target.value))} required>
            <option value="">Select</option>
            {caseStatusesList.map((s: any) => (
              <option key={s.id} value={s.id}>{s.case_status_name ?? s.id}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Priority">
          <select className={inputClass} value={priority} onChange={(e) => setPriority(e.target.value as 'High' | 'Medium' | 'Low')}>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
        </FormField>
        <FormField label="Court" hint="Required">
          <select
            className={inputClass}
            value={court_type === '' ? '' : court_type}
            onChange={(e) => {
              const v = e.target.value === '' ? '' : Number(e.target.value);
              setCourtType(v);
              setCourt('');
            }}
            required
          >
            <option value="">Select</option>
            {courtTypesList.map((t: any) => (
              <option key={t.id} value={t.id}>{t.court_type_name}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Registry" hint="Required">
          <select className={inputClass} value={court === '' ? '' : court} onChange={(e) => setCourt(e.target.value === '' ? '' : Number(e.target.value))} required disabled={typeof court_type !== 'number'}>
            <option value="">Select</option>
            {courtsList.map((c: any) => (
              <option key={c.id} value={c.id}>{c.court_name}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Judge" hint="Required">
          <select className={inputClass} value={judge_type === '' ? '' : judge_type} onChange={(e) => setJudgeType(e.target.value === '' ? '' : Number(e.target.value))} required>
            <option value="">Select</option>
            {judgesList.map((j: any) => (
              <option key={j.id} value={j.id}>{j.judge_name ?? j.id}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Date of service">
          <input className={inputClass} type="date" value={date_of_service} onChange={(e) => setDateOfService(e.target.value)} />
        </FormField>
        <FormField label="Remark">
          <input className={inputClass} value={remark} onChange={(e) => setRemark(e.target.value)} />
        </FormField>
        <FormField label="Description">
          <textarea className={inputClass} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </FormField>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Documents (optional)</label>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Attach files to this case. They will be uploaded after the case is created.</p>
          <div
            className="rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-600 p-4"
            onDrop={(e) => {
              e.preventDefault();
              const added = Array.from(e.dataTransfer.files || []);
              setDocuments((prev) => [...prev, ...added]);
            }}
            onDragOver={(e) => e.preventDefault()}
          >
            <input
              type="file"
              multiple
              className="sr-only"
              id="case-docs"
              onChange={(e) => setDocuments((prev) => [...prev, ...Array.from(e.target.files || [])])}
            />
            <label htmlFor="case-docs" className="cursor-pointer flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
              <FiUpload className="w-4 h-4" />
              <span>Add files</span>
            </label>
            {documents.length > 0 && (
              <ul className="mt-2 space-y-1">
                {documents.map((file, i) => (
                  <li key={i} className="flex items-center justify-between text-sm">
                    <span className="truncate text-slate-700 dark:text-slate-200">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => setDocuments((prev) => prev.filter((_, idx) => idx !== i))}
                      className="text-red-600 hover:text-red-700 text-xs"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="md:col-span-2 flex gap-2 mt-2">
          <Button variant="secondary" type="button" onClick={() => navigate(-1)}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!canSubmit || create.isPending}>{create.isPending ? 'Creating…' : 'Create'}</Button>
        </div>
      </form>
    </div>
  );
};

export default CourtCaseCreatePage;
